import React from 'react'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  ContentShare,
  FeaturedRemoteVideos,
  LocalVideo,
  RemoteVideos,
  VideoGrid,
  useContentShareState,
  useFeaturedTileState,
  useLocalVideo,
  useRemoteVideoTileState,
} from 'amazon-chime-sdk-component-library-react'
import { t } from '@lingui/core/macro'
import { LocalVideoOff } from './LocalVideoOff'

type VideoTileGridProps = {
  layout?: 'featured' | 'standard'
  localAttendeeName?: string
  noRemoteVideoView?: React.ReactNode
}

const fluidStyles = `
  height: 100%;
  width: 100%;
`

const collapsedStyles = `
  display: flex;
  position: absolute;
  bottom: 0.75rem;
  width: 25vw;
  width: 100%;
  height: 50%;
  cursor: default;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  @media (min-width: 768px) {
    top: 1.5rem;
    right: 1.5rem;
    bottom: auto;
    width: 20vw;
    min-width: 150px;
    max-width: 240px;
    max-height: 30vh;
  }
`

/** Camera-off placeholder slot, mirroring `collapsedStyles` (flush to the bottom on mobile). */
const localOffSx = {
  position: 'absolute',
  bottom: 0,
  width: '100%',
  height: '50%',
  zIndex: 5,
  overflow: 'hidden',
  borderRadius: { xs: 0, md: '12px' },
  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
  '@media (min-width: 768px)': {
    top: '1.5rem',
    right: '1.5rem',
    bottom: 'auto',
    width: '20vw',
    minWidth: '150px',
    maxWidth: '240px',
    maxHeight: '30vh',
  },
} as const

export function VideoTileGrid(props: VideoTileGridProps) {
  const { layout = 'featured', localAttendeeName, noRemoteVideoView } = props
  // The local self-view always identifies the current user as "Me".
  const localName = localAttendeeName ?? t`Me`
  const { tileId: contentTileId } = useContentShareState()
  const { tileId: featureTileId } = useFeaturedTileState()
  const { isVideoEnabled } = useLocalVideo()
  const { tiles } = useRemoteVideoTileState()
  const isDesktop = useMediaQuery('(min-width: 768px)')

  const featured = (layout === 'featured' && !!featureTileId) || !!contentTileId
  const remoteSize = tiles.length + (contentTileId ? 1 : 0)

  const splitView = !isDesktop && isVideoEnabled && remoteSize >= 1

  let gridSize: number
  if (splitView) gridSize = 2
  else if (remoteSize > 1 && isVideoEnabled) gridSize = remoteSize + 1
  else gridSize = Math.max(remoteSize, 1)

  let localCss: string
  if (splitView) localCss = fluidStyles
  else if (gridSize > 1 && remoteSize > 0) localCss = fluidStyles
  else localCss = collapsedStyles

  const gridLayout = splitView ? 'standard' : featured ? 'featured' : 'standard'
  const showPlaceholder = remoteSize === 0

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        '& > div': {
          flex: 1,
          width: '100% !important',
          height: '100% !important',
          minWidth: 0,
          minHeight: 0,
        },
        ...(splitView && {
          '& > div': {
            flex: 1,
            width: '100% !important',
            height: '100% !important',
            display: 'grid !important',
            gridTemplateColumns: '1fr !important',
            gridTemplateRows: '1fr 1fr !important',
            gap: '2px',
          },
        }),
      }}
    >
      <VideoGrid layout={gridLayout} size={gridSize}>
        {showPlaceholder && noRemoteVideoView}
        <ContentShare css="grid-area: ft;" />
        {layout === 'featured' && !splitView ? <FeaturedRemoteVideos /> : <RemoteVideos />}
        <LocalVideo nameplate={localName} css={localCss} />
        {!isVideoEnabled && !isDesktop && (
          <Box sx={localOffSx}>
            <LocalVideoOff name={localName} />
          </Box>
        )}
      </VideoGrid>
    </Box>
  )
}
